import { AvailabilityBadge, BadgeRow, VisibilityBadge } from "@/components/badge-row";
import { BadgeFilterCanvas, BadgeFilterPreviewCanvas } from "@/components/badge-filter-canvas";
import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { CopyId } from "@/components/copy-id";
import { UserPicker } from "@/components/user-picker";
import type { User } from "@/providers/users.provider";
import {
  type BadgeDto,
  type BadgeImageDto,
  type BadgeVariant,
  badgesProvider,
} from "@/providers/badges.provider";
import {
  IconAlertTriangle,
  IconChevronLeft,
  IconChevronRight,
  IconLoader2,
  IconMedal,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
  IconUserMinus,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import { formatImageInfo, useImageInfo } from "@/hooks/use-image-info";
import {
  ImageCompressionSlider,
  compressImageFile,
} from "@/components/image-compression-slider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE = 50;


// ── Image helpers ─────────────────────────────────────────────────────────────

function handleImageFile(
  file: File,
  onSuccess: (base64: string) => void,
  onError: (msg: string) => void,
) {
  if (!["image/jpeg", "image/png"].includes(file.type)) {
    onError("Only JPEG and PNG images are supported");
    return;
  }
  if (file.size > 500 * 1024) {
    onError("Image must be under 500 KB");
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const base64 = (reader.result as string).split(",")[1];
    onSuccess(base64);
  };
  reader.readAsDataURL(file);
}

async function compressToBase64(file: File, quality: number): Promise<string> {
  const compressed = await compressImageFile(file, quality);
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.readAsDataURL(compressed);
  });
}

// ── Existing image picker ───────────────────────────────────────────────────

function ExistingImagePicker({
  selected,
  onSelect,
}: {
  selected: BadgeImageDto | null;
  onSelect: (image: BadgeImageDto | null) => void;
}) {
  const [search, setSearch] = useState("");

  const { data: imagesPage, isLoading } = useQuery({
    queryKey: ["badge-images", 1, search],
    queryFn: () => badgesProvider.listImages(1, search || undefined),
    staleTime: 60_000,
  });

  const images = imagesPage?.data ?? [];

  return (
    <div className="flex flex-col gap-2">
      <Input
        placeholder="Search images…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8 text-xs"
      />
      <div className="flex items-center gap-3">
        <div className="size-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0">
          {selected ? (
            <img
              src={selected.img_url}
              alt={selected.name}
              className="size-full object-cover"
            />
          ) : (
            <IconMedal className="size-6 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <Select
            value={selected?.badge_image_id ?? ""}
            onValueChange={(id) => {
              const img = images.find((i) => i.badge_image_id === id) ?? null;
              onSelect(img);
            }}
          >
            <SelectTrigger className="w-full text-xs h-8">
              <SelectValue placeholder="Select an existing image" />
            </SelectTrigger>
            <SelectContent>
              {isLoading ? (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  Loading…
                </div>
              ) : !images.length ? (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  No images found.
                </div>
              ) : (
                images.map((image) => (
                  <SelectItem
                    key={image.badge_image_id}
                    value={image.badge_image_id}
                    className="text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src={image.img_url}
                        alt={image.name}
                        className="size-4 rounded object-cover"
                      />
                      {image.name}
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {selected && (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-destructive text-left mt-1"
              onClick={() => onSelect(null)}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Create badge sheet ────────────────────────────────────────────────────────

function CreateBadgeSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const [name, setName] = useState("");
  const [actionType, setActionType] = useState("");
  const [actionThreshold, setActionThreshold] = useState("");
  const [actionDescription, setActionDescription] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [availableFrom, setAvailableFrom] = useState("");
  const [availableTo, setAvailableTo] = useState("");
  const [existingImage, setExistingImage] = useState<BadgeImageDto | null>(
    null,
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      return badgesProvider.create({
        name: name.trim(),
        badge_image_id: existingImage!.badge_image_id,
        is_visible: isVisible,
        available_from: availableFrom
          ? new Date(availableFrom).toISOString()
          : null,
        available_to: availableTo ? new Date(availableTo).toISOString() : null,
        action_type: actionType.trim(),
        action_threshold: parseInt(actionThreshold, 10),
        action_description: actionDescription.trim(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badges"] });
      qc.invalidateQueries({ queryKey: ["badge-images"] });
      toast.success("Badge created");
      resetAndClose();
    },
    onError: (err) => toast.error(err.message),
  });

  function resetAndClose() {
    setName("");
    setActionType("");
    setActionThreshold("");
    setActionDescription("");
    setIsVisible(false);
    setAvailableFrom("");
    setAvailableTo("");
    setExistingImage(null);
    onClose();
  }

  function handleClose() {
    if (!isPending) resetAndClose();
  }

  const canSubmit =
    name.trim() &&
    existingImage !== null &&
    actionType.trim() &&
    actionDescription.trim() &&
    actionThreshold !== "" &&
    !isNaN(parseInt(actionThreshold, 10));

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New Badge</SheetTitle>
          <SheetDescription>
            Create a new badge users can earn. Add the image in the Images
            tab first, then pick it here.
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 flex flex-col gap-4">
          {/* Image */}
          <div className="flex flex-col gap-1.5">
            <Label>Image</Label>
            <ExistingImagePicker
              selected={existingImage}
              onSelect={setExistingImage}
            />
          </div>

          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-name">Name</Label>
            <Input
              id="new-badge-name"
              placeholder="e.g. Early Adopter"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Action type */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-action-type">Action type</Label>
            <Input
              id="new-badge-action-type"
              placeholder="e.g. distance_traveled"
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              className="font-mono text-sm"
            />
          </div>

          {/* Threshold */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-threshold">Action threshold</Label>
            <Input
              id="new-badge-threshold"
              type="number"
              min={0}
              placeholder="e.g. 1000"
              value={actionThreshold}
              onChange={(e) => setActionThreshold(e.target.value)}
            />
          </div>

          {/* Action description */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-action-desc">Action description</Label>
            <Textarea
              id="new-badge-action-desc"
              placeholder="e.g. Travel 1,000 meters"
              value={actionDescription}
              onChange={(e) => setActionDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Availability dates */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-from">Available from (optional)</Label>
            <Input
              id="new-badge-from"
              type="datetime-local"
              value={availableFrom}
              onChange={(e) => setAvailableFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-to">Available to (optional)</Label>
            <Input
              id="new-badge-to"
              type="datetime-local"
              value={availableTo}
              onChange={(e) => setAvailableTo(e.target.value)}
            />
          </div>

          {/* Visible */}
          <div className="flex items-center justify-between">
            <Label htmlFor="new-badge-visible">Visible to users</Label>
            <Switch
              id="new-badge-visible"
              checked={isVisible}
              onCheckedChange={setIsVisible}
            />
          </div>
        </div>

        <SheetFooter className="px-4">
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutate()} disabled={isPending || !canSubmit}>
            {isPending ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconPlus className="size-4" />
            )}
            Create
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ── Badge detail sheet ────────────────────────────────────────────────────────


function VariantRow({
  variant,
  badgeId,
  deleteMut,
}: {
  variant: BadgeVariant;
  badgeId: string;
  deleteMut: ReturnType<typeof useMutation<void, Error, string>>;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editDesc, setEditDesc] = useState(variant.variant_description ?? "");

  const updateMut = useMutation({
    mutationFn: (desc: string | null) =>
      badgesProvider.updateBadgeVariant(badgeId, variant.variant_id, desc),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variants", badgeId] });
      toast.success("Description updated");
      setEditing(false);
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <span className="font-mono shrink-0">{variant.variant_name}</span>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-1">
            <Input
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              className="h-6 text-xs py-0"
              autoFocus
              disabled={updateMut.isPending}
            />
            <button
              className="text-primary hover:underline shrink-0"
              onClick={() => updateMut.mutate(editDesc.trim() || null)}
              disabled={updateMut.isPending}
            >
              {updateMut.isPending ? (
                <IconLoader2 className="size-3 animate-spin" />
              ) : (
                "Save"
              )}
            </button>
            <button
              className="text-muted-foreground hover:underline shrink-0"
              onClick={() => {
                setEditDesc(variant.variant_description ?? "");
                setEditing(false);
              }}
              disabled={updateMut.isPending}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            className="text-muted-foreground hover:text-foreground truncate max-w-full text-left"
            onClick={() => setEditing(true)}
            title="Edit description"
          >
            {variant.variant_description ?? (
              <span className="italic">No description</span>
            )}
          </button>
        )}
      </div>
      {variant.variant_name !== "standard" && (
        <ConfirmDestructiveDialog
          confirmValue={variant.variant_id}
          confirmLabel="variant ID"
          title={`Remove variant "${variant.variant_name}"?`}
          description="This permanently removes the variant from this badge. This cannot be undone."
          actionLabel="Remove"
          onConfirm={() => deleteMut.mutate(variant.variant_id)}
          isPending={deleteMut.isPending}
          trigger={({ disabled }) => (
            <button
              className="text-muted-foreground hover:text-destructive transition-colors shrink-0 disabled:opacity-50"
              disabled={disabled}
              title="Remove variant"
            >
              <IconX className="size-3" />
            </button>
          )}
        />
      )}
    </div>
  );
}

function BadgeVariantsSection({ badge }: { badge: BadgeDto }) {
  const qc = useQueryClient();
  const [addVariantName, setAddVariantName] = useState("");
  const [addVariantDescription, setAddVariantDescription] = useState("");

  const { data: variants, isLoading: variantsLoading } = useQuery({
    queryKey: ["badge-variants", badge.badge_id],
    queryFn: () => badgesProvider.getVariantsForBadge(badge.badge_id),
  });

  const { data: variantTypes, isLoading: typesLoading } = useQuery({
    queryKey: ["badge-variant-types"],
    queryFn: () => badgesProvider.listVariantTypes(),
    staleTime: 5 * 60_000,
  });

  const existingNames = new Set(variants?.map((v) => v.variant_name) ?? []);
  const availableTypes =
    variantTypes?.filter((t) => !existingNames.has(t.name)) ?? [];

  const addMut = useMutation({
    mutationFn: () =>
      badgesProvider.addVariantToBadge(badge.badge_id, {
        variant_name: addVariantName,
        variant_description: addVariantDescription.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variants", badge.badge_id] });
      toast.success("Variant added");
      setAddVariantName("");
      setAddVariantDescription("");
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: (variantId: string) =>
      badgesProvider.deleteVariantFromBadge(badge.badge_id, variantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variants", badge.badge_id] });
      toast.success("Variant removed");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  return (
    <div className="px-4 flex flex-col gap-3">
      <h3 className="font-semibold text-sm">Variants</h3>

      {variantsLoading ? (
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col divide-y rounded-md border text-xs">
          {!variants?.length ? (
            <p className="px-3 py-2 text-muted-foreground">No variants yet.</p>
          ) : (
            variants.map((variant) => (
              <VariantRow
                key={variant.variant_id}
                variant={variant}
                badgeId={badge.badge_id}
                deleteMut={deleteMut}
              />
            ))
          )}
        </div>
      )}

      {availableTypes.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Select
              value={addVariantName}
              onValueChange={setAddVariantName}
              disabled={typesLoading || addMut.isPending}
            >
              <SelectTrigger className="flex-1 text-xs h-8">
                <SelectValue placeholder="Select variant type" />
              </SelectTrigger>
              <SelectContent>
                {availableTypes.map((t) => (
                  <SelectItem
                    key={t.name}
                    value={t.name}
                    className="text-xs font-mono"
                  >
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              disabled={!addVariantName || addMut.isPending}
              onClick={() => addMut.mutate()}
              className="shrink-0 gap-1.5"
            >
              {addMut.isPending ? (
                <IconLoader2 className="size-3.5 animate-spin" />
              ) : (
                <IconPlus className="size-3.5" />
              )}
              Add
            </Button>
          </div>
          <Input
            placeholder="Description (optional)"
            value={addVariantDescription}
            onChange={(e) => setAddVariantDescription(e.target.value)}
            disabled={!addVariantName || addMut.isPending}
            className="text-xs h-8"
          />
        </div>
      )}
    </div>
  );
}

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function BadgeDetailSheet({
  badge,
  onClose,
}: {
  badge: BadgeDto | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const badgeImageInfo = useImageInfo(badge?.img_url);

  const [grantUser, setGrantUser] = useState<User | null>(null);
  const [revokeUserId, setRevokeUserId] = useState("");
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [uploadQuality, setUploadQuality] = useState(85);

  // Edit form state
  const [actionThreshold, setActionThreshold] = useState("");
  const [actionDescription, setActionDescription] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [availableFrom, setAvailableFrom] = useState("");
  const [availableTo, setAvailableTo] = useState("");

  const prevBadgeId = useRef<string | null>(null);
  if (badge && badge.badge_id !== prevBadgeId.current) {
    prevBadgeId.current = badge.badge_id;
    setGrantUser(null);
    setRevokeUserId("");
    setActionThreshold(String(badge.action_threshold));
    setActionDescription(badge.action_description);
    setIsVisible(badge.is_visible);
    setAvailableFrom(toDatetimeLocal(badge.available_from));
    setAvailableTo(toDatetimeLocal(badge.available_to));
    setPendingUploadFile(null);
    setUploadQuality(85);
  }


  const updateMut = useMutation({
    mutationFn: () =>
      badgesProvider.update(badge!.badge_id, {
        is_visible: isVisible,
        available_from: availableFrom
          ? new Date(availableFrom).toISOString()
          : null,
        available_to: availableTo ? new Date(availableTo).toISOString() : null,
        action_threshold: parseInt(actionThreshold, 10),
        action_description: actionDescription.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badges"] });
      toast.success("Badge updated");
    },
    onError: (err) => toast.error(err.message),
  });

  const uploadMut = useMutation({
    mutationFn: async () => {
      const base64 = await compressToBase64(pendingUploadFile!, uploadQuality);
      return badgesProvider.uploadImage(badge!.badge_id, base64);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badges"] });
      toast.success("Image uploaded");
      setPendingUploadFile(null);
      setUploadQuality(85);
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => badgesProvider.delete(badge!.badge_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badges"] });
      toast.success("Badge deleted");
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const grantMut = useMutation({
    mutationFn: (userId: string) =>
      badgesProvider.grantToUser(badge!.badge_id, userId),
    onSuccess: () => {
      toast.success("Badge granted");
      setGrantUser(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const revokeMut = useMutation({
    mutationFn: (userId: string) =>
      badgesProvider.revokeFromUser(badge!.badge_id, userId),
    onSuccess: () => {
      toast.success("Badge revoked");
      setRevokeUserId("");
    },
    onError: (err) => toast.error(err.message),
  });

  const anyPending =
    deleteMut.isPending ||
    uploadMut.isPending ||
    updateMut.isPending ||
    grantMut.isPending ||
    revokeMut.isPending;

  return (
    <Sheet open={!!badge} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {badge && (
          <>
            <SheetHeader>
              <SheetTitle>{badge.name}</SheetTitle>
              <SheetDescription className="font-mono text-xs">
                {badge.badge_id}
              </SheetDescription>
            </SheetHeader>

            {/* Image */}
            <div className="px-4 flex flex-col gap-2">
              {badge.img_url ? (
                badge.filter_img_url ? (
                  <div
                    className="w-full rounded-lg border bg-muted overflow-hidden relative cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => fileRef.current?.click()}
                  >
                    <BadgeFilterCanvas
                      baseImageUrl={badge.img_url}
                      filterImageUrl={badge.filter_img_url}
                      size={400}
                      opacity={0.5}
                    />
                  </div>
                ) : (
                  <div
                    className="w-full rounded-lg border bg-muted overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => fileRef.current?.click()}
                  >
                    <img
                      src={badge.img_url}
                      alt={badge.name}
                      className="w-full h-auto"
                    />
                  </div>
                )
              ) : (
                <div
                  className="w-full rounded-lg border bg-muted flex items-center justify-center py-10 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => fileRef.current?.click()}
                >
                  <IconPhoto className="size-10 text-muted-foreground" />
                </div>
              )}

              {badgeImageInfo && (
                <p className="text-xs text-muted-foreground tabular-nums">
                  {formatImageInfo(badgeImageInfo)}
                </p>
              )}

              <div className="flex flex-col gap-2">
                {pendingUploadFile ? (
                  <>
                    <ImageCompressionSlider
                      file={pendingUploadFile}
                      quality={uploadQuality}
                      onChange={setUploadQuality}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => uploadMut.mutate()}
                        disabled={uploadMut.isPending}
                        className="gap-1.5"
                      >
                        {uploadMut.isPending ? (
                          <IconLoader2 className="size-3.5 animate-spin" />
                        ) : (
                          <IconUpload className="size-3.5" />
                        )}
                        Upload
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={uploadMut.isPending}
                        onClick={() => setPendingUploadFile(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploadMut.isPending}
                      className="gap-1.5"
                    >
                      <IconUpload className="size-3.5" />
                      {badge.img_url ? "Replace image" : "Upload image"}
                    </Button>
                  </>
                )}
              </div>

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (!file.type.startsWith("image/")) {
                    toast.error("Only image files are supported");
                    return;
                  }
                  setPendingUploadFile(file);
                  setUploadQuality(85);
                  e.target.value = "";
                }}
              />
            </div>

            <Separator />

            {/* Details */}
            <div className="px-4 flex flex-col gap-4">
              <h3 className="font-semibold text-sm">Details</h3>

              <div className="flex flex-col gap-1.5">
                <Label className="text-muted-foreground text-xs">Name</Label>
                <p className="text-sm">{badge.name}</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-muted-foreground text-xs">
                  Action type
                </Label>
                <p className="text-sm font-mono">{badge.action_type}</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-threshold" className="text-xs">
                  Action threshold
                </Label>
                <Input
                  id="edit-threshold"
                  type="number"
                  min={0}
                  value={actionThreshold}
                  onChange={(e) => setActionThreshold(e.target.value)}
                  disabled={updateMut.isPending}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-action-desc" className="text-xs">
                  Action description
                </Label>
                <Textarea
                  id="edit-action-desc"
                  value={actionDescription}
                  onChange={(e) => setActionDescription(e.target.value)}
                  rows={2}
                  disabled={updateMut.isPending}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-from" className="text-xs">
                  Available from
                </Label>
                <Input
                  id="edit-from"
                  type="datetime-local"
                  value={availableFrom}
                  onChange={(e) => setAvailableFrom(e.target.value)}
                  disabled={updateMut.isPending}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-to" className="text-xs">
                  Available to
                </Label>
                <Input
                  id="edit-to"
                  type="datetime-local"
                  value={availableTo}
                  onChange={(e) => setAvailableTo(e.target.value)}
                  disabled={updateMut.isPending}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="edit-visible" className="text-xs">
                  Visible to users
                </Label>
                <Switch
                  id="edit-visible"
                  checked={isVisible}
                  onCheckedChange={setIsVisible}
                  disabled={updateMut.isPending}
                />
              </div>

              <Button
                size="sm"
                disabled={
                  updateMut.isPending ||
                  !actionDescription.trim() ||
                  actionThreshold === "" ||
                  isNaN(parseInt(actionThreshold, 10))
                }
                onClick={() => updateMut.mutate()}
                className="self-start gap-1.5"
              >
                {updateMut.isPending ? (
                  <IconLoader2 className="size-3.5 animate-spin" />
                ) : null}
                Save changes
              </Button>
            </div>

            <Separator />

            {/* Variants */}
            <BadgeVariantsSection badge={badge} />

            <Separator />

            {/* Grant */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm">Grant to user</h3>
              <div className="flex flex-col gap-2">
                <UserPicker
                  selected={grantUser}
                  onSelect={setGrantUser}
                  placeholder="Search by username…"
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={grantMut.isPending || !grantUser}
                  onClick={() => grantMut.mutate(grantUser!.user_id)}
                  className="self-start gap-1.5"
                >
                  {grantMut.isPending ? (
                    <IconLoader2 className="size-3.5 animate-spin" />
                  ) : (
                    <IconUserPlus className="size-3.5" />
                  )}
                  Grant
                </Button>
              </div>
            </div>

            <Separator />

            {/* Revoke */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm">Revoke from user</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="User ID"
                  value={revokeUserId}
                  onChange={(e) => setRevokeUserId(e.target.value)}
                  className="font-mono text-xs"
                />
                <ConfirmDestructiveDialog
                  key={revokeUserId}
                  confirmValue={revokeUserId.trim()}
                  confirmLabel="user ID"
                  title="Revoke this badge from user?"
                  description="This removes the badge from this user. This cannot be undone."
                  actionLabel="Revoke"
                  onConfirm={() => revokeMut.mutate(revokeUserId.trim())}
                  isPending={revokeMut.isPending}
                  trigger={({ disabled }) => (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={disabled || !revokeUserId.trim()}
                      className="shrink-0 gap-1.5"
                    >
                      {revokeMut.isPending ? (
                        <IconLoader2 className="size-3.5 animate-spin" />
                      ) : (
                        <IconUserMinus className="size-3.5" />
                      )}
                      Revoke
                    </Button>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Delete */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm text-destructive">
                Danger zone
              </h3>
              <ConfirmDestructiveDialog
                confirmValue={badge.badge_id}
                confirmLabel="badge ID"
                title={`Delete "${badge.name}"?`}
                description="This will permanently delete the badge and remove it from all users. This cannot be undone."
                actionLabel="Delete badge"
                onConfirm={() => deleteMut.mutate()}
                isPending={deleteMut.isPending}
                trigger={({ disabled }) => (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/30 hover:bg-destructive/10 self-start gap-1.5"
                    disabled={disabled || anyPending}
                  >
                    <IconTrash className="size-3.5" />
                    Delete badge
                  </Button>
                )}
              />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Variant types tab ─────────────────────────────────────────────────────────

function VariantTypeImageUpload({
  variantName,
  hasFilter,
}: {
  variantName: string;
  hasFilter: boolean;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const uploadMut = useMutation({
    mutationFn: (base64: string) =>
      badgesProvider.uploadVariantTypeImage(variantName, base64),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variant-types"] });
      toast.success("Filter image updated");
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex items-center gap-1.5 justify-end">
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 text-xs"
        disabled={uploadMut.isPending}
        onClick={() => fileRef.current?.click()}
      >
        {uploadMut.isPending ? (
          <IconLoader2 className="size-3 animate-spin" />
        ) : (
          <IconUpload className="size-3" />
        )}
        {uploadMut.isPending
          ? "Uploading…"
          : hasFilter
            ? "Replace"
            : "Upload image"}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          handleImageFile(
            file,
            (base64) => uploadMut.mutate(base64),
            (msg) => toast.error(msg),
          );
          e.target.value = "";
        }}
      />
    </div>
  );
}

function VariantTypesTab() {
  const qc = useQueryClient();
  const createFileRef = useRef<HTMLInputElement>(null);

  const [newName, setNewName] = useState("");
  const [newFilterFile, setNewFilterFile] = useState<{
    file: File;
    base64: string;
  } | null>(null);

  const {
    data: variantTypes,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["badge-variant-types"],
    queryFn: () => badgesProvider.listVariantTypes(),
    staleTime: 5 * 60_000,
  });

  const createMut = useMutation({
    mutationFn: () =>
      badgesProvider.createVariantType(newName.trim(), newFilterFile?.base64),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variant-types"] });
      toast.success("Variant type created");
      setNewName("");
      setNewFilterFile(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const canCreate = newName.trim().length > 0;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Global dictionary of variant names available across all badges. A badge
        can only be given a variant that exists here.
      </p>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Filter overlay</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="py-10 text-center text-muted-foreground"
                >
                  Failed to load variant types.
                </TableCell>
              </TableRow>
            ) : !variantTypes?.length ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="py-10 text-center text-muted-foreground"
                >
                  No variant types found.
                </TableCell>
              </TableRow>
            ) : (
              variantTypes.map((vt) => (
                <TableRow key={vt.name}>
                  <TableCell>
                    <Badge
                      variant={vt.name === "standard" ? "secondary" : "outline"}
                      className="font-mono text-xs"
                    >
                      {vt.name}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {vt.filter_img_url ? (
                      <div className="flex items-center gap-2">
                        <img
                          src={vt.filter_img_url}
                          alt={vt.name}
                          className="size-6 rounded border object-cover"
                        />
                        <span className="text-xs text-muted-foreground font-mono truncate max-w-72">
                          {vt.filter_img_url}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        None
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <VariantTypeImageUpload
                      variantName={vt.name}
                      hasFilter={!!vt.filter_img_url}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create new variant type */}
      <div className="rounded-lg border p-4 flex flex-col gap-3">
        <h3 className="font-semibold text-sm">New variant type</h3>
        <div className="flex gap-2 items-end">
          <div className="flex flex-col gap-1.5 flex-1">
            <Label htmlFor="new-vt-name" className="text-xs">
              Name
            </Label>
            <Input
              id="new-vt-name"
              placeholder="e.g. gold"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="font-mono text-sm h-8"
              disabled={createMut.isPending}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Filter image (optional)</Label>
            <div className="flex items-center gap-2">
              <div
                className="size-8 rounded border bg-muted flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity shrink-0"
                onClick={() => createFileRef.current?.click()}
              >
                {newFilterFile ? (
                  <img
                    src={URL.createObjectURL(newFilterFile.file)}
                    alt="preview"
                    className="size-full object-cover"
                  />
                ) : (
                  <IconPhoto className="size-4 text-muted-foreground" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => createFileRef.current?.click()}
                disabled={createMut.isPending}
              >
                <IconUpload className="size-3" />
                {newFilterFile ? "Change" : "Choose"}
              </Button>
              {newFilterFile && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => setNewFilterFile(null)}
                >
                  <IconX className="size-3.5" />
                </button>
              )}
            </div>
          </div>
          <Button
            size="sm"
            className="h-8 gap-1.5"
            disabled={!canCreate || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? (
              <IconLoader2 className="size-3.5 animate-spin" />
            ) : (
              <IconPlus className="size-3.5" />
            )}
            Create
          </Button>
        </div>
        <input
          ref={createFileRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            handleImageFile(
              file,
              (base64) => setNewFilterFile({ file, base64 }),
              (msg) => toast.error(msg),
            );
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ── Images tab ────────────────────────────────────────────────────────────────

function DeleteImageAction({ image }: { image: BadgeImageDto }) {
  const qc = useQueryClient();

  const deleteMut = useMutation({
    mutationFn: (badgeImageId: string) =>
      badgesProvider.deleteImage(badgeImageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-images"] });
      toast.success("Image deleted");
    },
    onError: () =>
      toast.error(
        "Failed to delete image. It may still be granted to users — revoke those grants first.",
      ),
  });

  return (
    <ConfirmDestructiveDialog
      confirmValue={image.badge_image_id}
      confirmLabel="image ID"
      title={`Delete image "${image.name}"?`}
      description="This permanently deletes the image and every badge variant using it, along with their event associations. This cannot be undone. Badges with active user grants cannot be deleted — revoke those grants first."
      actionLabel="Delete image"
      onConfirm={() => deleteMut.mutate(image.badge_image_id)}
      isPending={deleteMut.isPending}
      trigger={({ disabled }) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-destructive hover:text-destructive"
          disabled={disabled}
        >
          <IconTrash className="size-3.5" />
        </Button>
      )}
    >
      <img
        src={image.img_url}
        alt={image.name}
        className="w-full h-auto max-h-48 object-contain rounded-lg border bg-muted"
      />
    </ConfirmDestructiveDialog>
  );
}

function ImagePreviewDialog({
  image,
  onClose,
}: {
  image: BadgeImageDto | null;
  onClose: () => void;
}) {
  const info = useImageInfo(image?.img_url);

  return (
    <Dialog open={!!image} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl">
        {image && (
          <>
            <DialogHeader>
              <DialogTitle>{image.name}</DialogTitle>
            </DialogHeader>
            <img
              src={image.img_url}
              alt={image.name}
              className="w-full h-auto max-h-[70vh] object-contain rounded-lg border bg-muted"
            />
            {info && (
              <p className="text-xs text-muted-foreground text-center">
                {formatImageInfo(info)}
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateImageDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [quality, setQuality] = useState(85);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const created = await badgesProvider.createImage(name.trim());
      const compressed = await compressImageFile(imageFile!, quality);
      const compressedFile = new File([compressed], imageFile!.name, {
        type: compressed.type,
      });
      await badgesProvider.uploadToPresignedUrl(created, compressedFile);
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-images"] });
      toast.success("Image created");
      resetAndClose();
    },
    onError: (err) => toast.error(err.message),
  });

  function resetAndClose() {
    setName("");
    setImageFile(null);
    setQuality(85);
    onClose();
  }

  function handleClose() {
    if (!isPending) resetAndClose();
  }

  const canSubmit = name.trim() && imageFile !== null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Image</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div
              className="size-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => fileRef.current?.click()}
            >
              {imageFile ? (
                <img
                  src={URL.createObjectURL(imageFile)}
                  alt="preview"
                  className="size-full object-cover"
                />
              ) : (
                <IconPhoto className="size-6 text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                className="gap-1.5"
              >
                <IconUpload className="size-3.5" />
                {imageFile ? "Change image" : "Choose image"}
              </Button>
              {imageFile && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-destructive text-left"
                  onClick={() => setImageFile(null)}
                >
                  Remove
                </button>
              )}
              <p className="text-xs text-muted-foreground">Image, max 5 MB</p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!file.type.startsWith("image/")) {
                  toast.error("Only image files are supported");
                  return;
                }
                if (file.size > 5 * 1024 * 1024) {
                  toast.error("Image must be under 5 MB");
                  return;
                }
                setImageFile(file);
                e.target.value = "";
              }}
            />
          </div>

          {imageFile && (
            <ImageCompressionSlider
              file={imageFile}
              quality={quality}
              onChange={setQuality}
            />
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-image-name">Name</Label>
            <Input
              id="new-image-name"
              placeholder="e.g. Early Adopter"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutate()} disabled={isPending || !canSubmit}>
            {isPending ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconPlus className="size-4" />
            )}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImagesTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [previewTarget, setPreviewTarget] = useState<BadgeImageDto | null>(
    null,
  );
  const [createOpen, setCreateOpen] = useState(false);

  const {
    data: imagesPage,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["badge-images", page, search],
    queryFn: () => badgesProvider.listImages(page, search || undefined),
    staleTime: 60_000,
  });

  const images = imagesPage?.data ?? [];
  const hasMore = imagesPage?.has_next ?? false;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Image assets used by badges. A single image can be shared across
        multiple badge variants.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            placeholder="Search by name…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
          <IconPlus className="size-4" />
          New image
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Image ID</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 4 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="py-12 text-center text-muted-foreground"
                >
                  Failed to load badge images.
                </TableCell>
              </TableRow>
            ) : !images.length ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="py-12 text-center text-muted-foreground"
                >
                  No badge images found.
                </TableCell>
              </TableRow>
            ) : (
              images.map((image) => (
                <TableRow key={image.badge_image_id}>
                  <TableCell>
                    <img
                      src={image.img_url}
                      alt={image.name}
                      className="size-8 rounded border object-cover cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setPreviewTarget(image)}
                    />
                  </TableCell>
                  <TableCell>{image.name}</TableCell>
                  <TableCell>
                    <CopyId id={image.badge_image_id} />
                  </TableCell>
                  <TableCell className="text-right">
                    <DeleteImageAction image={image} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <CreateImageDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <ImagePreviewDialog image={previewTarget} onClose={() => setPreviewTarget(null)} />

      {(page > 1 || hasMore) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="gap-1"
          >
            <IconChevronLeft className="size-3.5" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">Page {page}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasMore}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next
            <IconChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function BadgesPage() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<"all" | "event" | "static">("all");
  const [selectedBadge, setSelectedBadge] = useState<BadgeDto | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const {
    data: badgesPage,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["badges", page, filter],
    queryFn: () =>
      badgesProvider.listAll(page, filter === "all" ? undefined : filter),
    staleTime: 60_000,
  });

  const paginated = badgesPage?.data ?? [];
  const hasMore = badgesPage?.has_next ?? false;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Badges" />

      <div className="flex flex-col gap-4 flex-1 p-4 lg:p-6">
        <Tabs defaultValue="badges">
          <TabsList>
            <TabsTrigger value="badges">Badges</TabsTrigger>
            <TabsTrigger value="images">Images</TabsTrigger>
            <TabsTrigger value="variant-types">Variant Types</TabsTrigger>
          </TabsList>

          <TabsContent value="badges" className="flex flex-col gap-4 mt-4">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <IconMedal className="size-4 text-muted-foreground" />
                <Select
                  value={filter}
                  onValueChange={(v) => {
                    setFilter(v as "all" | "event" | "static");
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 text-xs w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">All badges</SelectItem>
                    <SelectItem value="event" className="text-xs">Event badges</SelectItem>
                    <SelectItem value="static" className="text-xs">Static badges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                onClick={() => setCreateOpen(true)}
                className="gap-1.5"
              >
                <IconPlus className="size-4" />
                New badge
              </Button>
            </div>

            {/* Table */}
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Badge ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Variant</TableHead>
                    <TableHead>Action type</TableHead>
                    <TableHead>Threshold</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Visibility</TableHead>
                    <TableHead>Availability</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : isError ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-12 text-center text-muted-foreground"
                      >
                        Failed to load badges. Check your connection and try
                        again.
                      </TableCell>
                    </TableRow>
                  ) : !paginated.length ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No badges yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((badge) => (
                      <BadgeRow
                        key={badge.badge_id}
                        badge={badge}
                        onClick={() => setSelectedBadge(badge)}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {(page > 1 || hasMore) && (
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="gap-1"
                >
                  <IconChevronLeft className="size-3.5" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasMore}
                  onClick={() => setPage((p) => p + 1)}
                  className="gap-1"
                >
                  Next
                  <IconChevronRight className="size-3.5" />
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="images" className="mt-4">
            <ImagesTab />
          </TabsContent>

          <TabsContent value="variant-types" className="mt-4">
            <VariantTypesTab />
          </TabsContent>
        </Tabs>
      </div>

      <CreateBadgeSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <BadgeDetailSheet
        badge={selectedBadge}
        onClose={() => setSelectedBadge(null)}
      />
    </div>
  );
}
